import React, { useState } from 'react'
import { useTeamEventsQuery } from './TeamEvents.generated-types'
import { Typography, Table, Input } from 'antd'
import { s } from './style'
import { DateTime } from 'luxon'

const { Text, Title } = Typography
const { Search } = Input

export const TeamEvents: React.FC = (): JSX.Element => {
  const [filter, setFilter] = useState('')

  const onSearch = (value: string): void => setFilter(value)

  const [{ fetching, data }] =
  useTeamEventsQuery({
    requestPolicy: 'cache-and-network',
  })

  const mapStatusToDisplayStatus = (event): string => {
    // Past event - event date is in the past
    // Booked - requested_to_book_at is not null and status is not cancelled
    // Cancelled - status is cancelled
    // Live enquiry - all other statuses
    const d = DateTime.fromISO(event.fromDateTime)
    if (d < DateTime.now()) return 'Past event'

    if (event.requestedToBookAt && event.status !== 'Cancelled') return 'Booked'

    if (event.status === 'Cancelled') return 'Cancelled'

    return 'Live enquiry'
  }

  const buildTeamFilter = (events: any[]): any[] => {
    if (!events) return []

    const teams = events.map((e) => e.team.name)
    const uniq = Array.from(new Set(teams)).sort((a, b) => a.localeCompare(b))

    return uniq.map((t) => ({
      text: t,
      value: t
    }))
  }

  const buildGuestFilter = (events: any[]): any[] => {
    if (!events) return []

    const guests = events.map((e) => guestFullName(e))
    const uniq = Array.from(new Set(guests)).sort((a, b) => a.localeCompare(b))

    return uniq.map((t) => ({
      text: t,
      value: t
    }))
  }

  const buildStatusFilter = (events: any[]): any[] => {
    if (!events) return []

    const statuses = events.map((e) => mapStatusToDisplayStatus(e))
    const uniq = Array.from(new Set(statuses)).sort((a, b) => a.localeCompare(b))

    return uniq.map((t) => ({
      text: t,
      value: t
    }))
  }

  const guestFullName = (e): string => `${e.guest.firstName} ${e.guest.lastName}`

  const filterEventsByName = (events): any[] => {
    return events.filter((e) => {
      return e.name.toLowerCase().includes(filter?.toLowerCase())
    }
    )
  }

  // date, team, name, organiser, status, actions
  const columns = [
    {
      title: 'Date',
      dataIndex: 'fromDateTime',
      key: 'name',
      render: (ID, row) => DateTime.fromISO(row.fromDateTime, { zone: 'utc' }).toLocaleString(DateTime.DATE_MED)
    },
    {
      title: 'Team',
      dataIndex: 'team',
      key: 'ID',
      render: (ID, row) => <strong>{row.team.name}</strong>,
      filters: buildTeamFilter(data?.getEventsByUsersTeams),
      onFilter: (value: string, record) => record.team.name === value
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => <>{name}<br/><Text style={s.location}>{row.location}</Text></>,
    },
    {
      title: 'Organiser',
      dataIndex: 'organiser',
      key: 'organiser',
      render: (guest, row) => <>{guestFullName(row)}</>,
      filters: buildGuestFilter(data?.getEventsByUsersTeams),
      onFilter: (value: string, record) => guestFullName(record) === value
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (ID, row) => <div style={s.statusTag}>{mapStatusToDisplayStatus(row)}</div>,
      filters: buildStatusFilter(data?.getEventsByUsersTeams),
      onFilter: (value: string, record) => mapStatusToDisplayStatus(record) === value
    },
    {
      title: 'Actions',
      dataIndex: 'UUID',
      key: 'UUID',
      render: (UUID) => <a href={`/smart-book-events/event/${UUID}`} target="_blank" rel="noreferrer">View event</a>,
    },
  ]

  if (fetching) return (<div/>)

  return (

    <div style={s.root}>
      <Title level={3} style={s.title}>Team events</Title>
      <Search size="large"
        placeholder="Filter events by name"
        allowClear
        onSearch={onSearch}
        style={{ position: 'absolute', right: 0, width: 400, marginTop: 10 }}
      />
      {
        data.getEventsByUsersTeams.length === 0 && (
          <Title level={5} style={s.noEventsTitle}>Events belonging to other members of your team(s) will show here</Title>
        )
      }
      <Table rowKey="ID" columns={columns} dataSource={filterEventsByName(data.getEventsByUsersTeams)}></Table>
    </div>

  )
}
